import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {DatePipe} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {Datenquelle, LadeBericht, LadeQuellenService} from '../../../service/lade-quellen.service';
import {GrafikUmfang} from '../../../model/event-bus.model';

/**
 * Der blaugraue Kasten nach "MSWR-MOS laden": welche Datenquellen für den Umfang geladen
 * wurden und von wann die Daten sind.
 *
 * Das Gegenstück zur grünen KI-Box – gleicher Platz (unter der Leiste oben für den Reiter,
 * über jeder Zeile für deren Knopf), andere Farbe, damit "grün = KI-Text" bleibt. Welche
 * Meldungen er zeigt, entscheidet allein der {@link GrafikUmfang}, den er bekommt.
 */
@Component({
  selector: 'app-lade-quellen-box',
  templateUrl: './lade-quellen-box.component.html',
  styleUrls: ['./lade-quellen-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatIconModule, DatePipe]
})
export class LadeQuellenBoxComponent implements OnInit {

  /** Der Umfang, dessen Bericht gezeigt wird – ein stabiles Objekt, s. KiErgebnisBoxComponent. */
  @Input() umfang: GrafikUmfang | null = null;

  constructor(private ladeQuellenService: LadeQuellenService) {
  }

  ngOnInit(): void {
    if (!LadeQuellenService.eingeschaltet) {
      return;   // abgeschaltet (environment.showLadeQuellen): die Box bleibt bewusst leer
    }
    if (!this.umfang) {
      console.warn('Lade-Quellen-Box ohne [umfang]: sie kann nie einen Bericht zeigen.');
    }
  }

  /** Liest ein Signal – die Ansicht wird neu geprüft, sobald eine Karte meldet. */
  get bericht(): LadeBericht | null {
    return this.ladeQuellenService.berichtFuer(this.umfang);
  }

  schliessen(): void {
    this.ladeQuellenService.schliessen(this.umfang);
  }

  icon(quelle: Datenquelle): string {
    switch (quelle.art) {
      case 'mos': return 'description';
      case 'snapshot': return 'storage';
      default: return 'sync';
    }
  }

  /** Lesbare Bezeichnung der Quelle: Dateiname bzw. Herkunft der Stationswerte. */
  beschreibung(quelle: Datenquelle): string {
    switch (quelle.art) {
      case 'mos': return `MOS-Datei ${quelle.name}`;
      case 'snapshot': return 'MSWR-Stationsvorhersagen aus dem Snapshot des Rest-Service';
      default: return 'MSWR-Stationsvorhersagen einzeln von der Wetterdaten-API';
    }
  }

  /**
   * Liegen ältester und jüngster Zeitstempel mehr als eine Minute auseinander? Dann nennt der
   * Kasten beide – sonst nur den jüngsten, ein Refresh schreibt alle Einträge binnen Sekunden.
   */
  deutlichAelter(aelteste: string | null, neueste: string | null): boolean {
    if (!aelteste || !neueste) {
      return false;
    }
    return new Date(neueste).getTime() - new Date(aelteste).getTime() > 60_000;
  }

  /** Was der Stand bedeutet, wenn keiner da ist – je Art verschieden. */
  ohneStand(quelle: Datenquelle): string {
    return quelle.art === 'einzelabruf'
      ? 'Stand: Abrufzeitpunkt (die API liefert keinen Zeitstempel mit)'
      : 'Stand unbekannt – die Quelle liefert keinen Zeitstempel';
  }
}
